import { Activity, Occasion } from '@oskarjiang/matchi-types';

/**
 * Filter state interface - defines the shape of all available filters
 */
export interface FilterState {
	showOnlyEmpty: boolean;
	showAfterWork: boolean;
	ladiesOnly: boolean;
	seniorOnly: boolean;
	mixedOnly: boolean;
	privateLesson: boolean;
	showMachine: boolean;
	hideLateEvening: boolean;
	minLevel: number | null;
	searchTerm: string;
	hiddenFacilities: number[];
}

/**
 * Filter function type - takes an occasion, related activity, and filter state
 */
export type FilterFunction = (occasion: Occasion, activity: Activity | undefined, state: FilterState) => boolean;

/**
 * Filter metadata for configuration and UI purposes
 */
export interface FilterMetadata {
	key: keyof FilterState;
	label: string;
	description: string;
	/** If true, this filter bypasses the minLevel restriction when active */
	bypassesMinLevel?: boolean;
}

/**
 * Configuration for all available filters
 */
export const FILTER_CONFIGS: FilterMetadata[] = [
	{
		key: 'showOnlyEmpty',
		label: 'Only Empty',
		description: 'Show only slots with no participants',
		bypassesMinLevel: true,
	},
	{
		key: 'showAfterWork',
		label: 'After Work',
		description: 'Filter for after work hours (16:00 - 19:00)',
		bypassesMinLevel: true,
	},
	{
		key: 'ladiesOnly',
		label: 'Ladies',
		description: 'Show sessions targeted to ladies',
		bypassesMinLevel: true,
	},
	{
		key: 'seniorOnly',
		label: 'Senior',
		description: 'Show senior sessions',
		bypassesMinLevel: true,
	},
	{
		key: 'mixedOnly',
		label: 'Mixed',
		description: 'Show mixed sessions',
		bypassesMinLevel: true,
	},
	{
		key: 'privateLesson',
		label: 'Lessons',
		description: 'Show private lessons only',
		bypassesMinLevel: true,
	},
	{
		key: 'showMachine',
		label: 'Show Machine',
		description: 'Include machine sessions',
		bypassesMinLevel: true,
	},
	{
		key: 'hideLateEvening',
		label: 'Ends Before 21:00',
		description: 'Hide sessions that end 21:00 or later',
	},
];

/**
 * Determines if any filters that bypass minLevel are currently active
 */
export const hasActiveBypassFilters = (state: FilterState): boolean => {
	return (
		FILTER_CONFIGS.some((config) => {
			if (!config.bypassesMinLevel) return false;
			const value = state[config.key];
			return typeof value === 'boolean' ? value : false;
		}) || state.searchTerm.length > 0
	);
};
